import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Avatar,
    Stack,
    Alert,
    IconButton,
    Tooltip,
    LinearProgress,
    Fade,
    Badge,
    Menu
} from '@mui/material';
import {
    Feedback,
    CheckCircle,
    HelpOutline,
    Visibility,
    ThumbUp,
    ThumbDown,
    Person,
    Search,
    FilterList,
    Refresh,
    Info,
    Download,
    SmartToy,
    PhotoCamera,
    GetApp
} from '@mui/icons-material';
import { adminFeedbackAPI } from '../../api/adminFeedback';
import FeedbackResolutionDialog from './feedback/FeedbackResolutionDialog';
import FeedbackDetailsDialog from './feedback/FeedbackDetailsDialog';

const AdminFeedbackManagement = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('');
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [resolutionDialog, setResolutionDialog] = useState(false);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [adminResponse, setAdminResponse] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [exportMenu, setExportMenu] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchFeedbacks();
    }, [page, size, statusFilter, searchTerm, feedbackTypeFilter]);

    const fetchFeedbacks = async () => {
        try {
            setLoading(true);
            const response = await adminFeedbackAPI.getAllFeedback(page, size, statusFilter, searchTerm, feedbackTypeFilter);
            setFeedbacks(response.data.content || []);
            setTotalElements(response.data.totalElements || 0);
        } catch (error) {
            console.error('Failed to fetch feedbacks:', error);
            showAlert('Failed to load feedback data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleResolveFeedback = async (responseText) => {
        if (!selectedFeedback || !responseText?.trim()) {
            showAlert('Please provide an admin response', 'warning');
            return;
        }

        try {
            setSubmitting(true);
            await adminFeedbackAPI.resolveFeedback(selectedFeedback.id, responseText);
            showAlert('Feedback resolved successfully!', 'success');
            setResolutionDialog(false);
            setAdminResponse('');
            setSelectedFeedback(null);
            fetchFeedbacks(); // Refresh data
        } catch (error) {
            console.error('Failed to resolve feedback:', error);
            showAlert('Failed to resolve feedback', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusUpdate = async (feedbackId, newStatus) => {
        try {
            await adminFeedbackAPI.updateFeedbackStatus(feedbackId, newStatus);
            showAlert(`Feedback status updated to ${newStatus}`, 'success');
            fetchFeedbacks(); // Refresh data
        } catch (error) {
            console.error('Failed to update status:', error);
            showAlert('Failed to update status', 'error');
        }
    };

    const handleExport = async (exportType) => {
        try {
            setExporting(true);
            let response;
            let filename;

            switch (exportType) {
                case 'incorrect':
                    response = await adminFeedbackAPI.exportIncorrectFeedback();
                    filename = 'incorrect_feedback_export.json';
                    break;
                case 'high-priority':
                    response = await adminFeedbackAPI.exportHighPriorityFeedback();
                    filename = 'high_priority_feedback_export.json';
                    break;
                case 'all':
                    response = await adminFeedbackAPI.exportAllFeedback();
                    filename = 'all_feedback_export.json';
                    break;
                default:
                    throw new Error('Invalid export type');
            }

            // Create download link
            const blob = new Blob([response.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showAlert(`Successfully exported ${exportType} feedback data for AI training`, 'success');
        } catch (error) {
            console.error('Export failed:', error);
            showAlert('Failed to export feedback data', 'error');
        } finally {
            setExporting(false);
            setExportMenu(null);
        }
    };

    const showAlert = (message, severity = 'success') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 5000);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'OPEN': return '#ff9800';
            case 'REVIEWED': return '#2196f3';
            case 'RESOLVED': return '#4caf50';
            default: return '#757575';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'OPEN': return <HelpOutline />;
            case 'REVIEWED': return <Visibility />;
            case 'RESOLVED': return <CheckCircle />;
            default: return <HelpOutline />;
        }
    };

    const getSeverityColor = (confidence) => {
        if (confidence > 90) return '#f44336'; // High confidence but wrong - critical
        if (confidence > 70) return '#ff9800'; // Medium priority
        return '#4caf50'; // Low priority
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const openDetailsDialog = async (feedback) => {
        try {
            const response = await adminFeedbackAPI.getFeedbackDetails(feedback.id);
            setSelectedFeedback(response.data);
            setDetailsDialog(true);
        } catch (error) {
            console.error('Failed to fetch feedback details:', error);
            showAlert('Failed to load feedback details', 'error');
        }
    };

    const handleResolveClick = () => {
        setDetailsDialog(false);
        setResolutionDialog(true);
        setAdminResponse(selectedFeedback?.response || '');
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper elevation={4} sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Avatar sx={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '3px solid rgba(255,255,255,0.3)',
                            width: 80,
                            height: 80
                        }}>
                            <Feedback sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
                                🤖 AI Training Feedback Center
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                Review feedback and export training data to improve AI accuracy
                            </Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={(e) => setExportMenu(e.currentTarget)}
                            disabled={exporting}
                            sx={{
                                background: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.3)'
                                }
                            }}
                        >
                            Export AI Training Data
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Refresh />}
                            onClick={fetchFeedbacks}
                            sx={{
                                background: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.3)'
                                }
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportMenu}
                open={Boolean(exportMenu)}
                onClose={() => setExportMenu(null)}
                PaperProps={{
                    sx: { borderRadius: 2, mt: 1 }
                }}
            >
                <MenuItem onClick={() => handleExport('incorrect')} disabled={exporting}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToy sx={{ color: '#f44336' }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>Incorrect Predictions</Typography>
                            <Typography variant="caption" color="text.secondary">
                                All wrong classifications for model training
                            </Typography>
                        </Box>
                    </Box>
                </MenuItem>
                <MenuItem onClick={() => handleExport('high-priority')} disabled={exporting}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToy sx={{ color: '#ff9800' }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>High Priority</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Critical cases (high confidence but wrong)
                            </Typography>
                        </Box>
                    </Box>
                </MenuItem>
                <MenuItem onClick={() => handleExport('all')} disabled={exporting}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GetApp sx={{ color: '#4caf50' }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>All Feedback</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Complete dataset for analysis
                            </Typography>
                        </Box>
                    </Box>
                </MenuItem>
            </Menu>

            {/* Alert */}
            <Fade in={alert.show}>
                <Box sx={{ mb: 2 }}>
                    {alert.show && (
                        <Alert
                            severity={alert.severity}
                            sx={{ borderRadius: 2 }}
                            onClose={() => setAlert({ show: false, message: '', severity: 'success' })}
                        >
                            {alert.message}
                        </Alert>
                    )}
                </Box>
            </Fade>

            {/* Filters */}
            <Card elevation={3} sx={{ mb: 3, borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterList /> Filter Options
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Search feedback"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                                placeholder="Search by message content..."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Status Filter</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status Filter"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="OPEN">Open</MenuItem>
                                    <MenuItem value="REVIEWED">Reviewed</MenuItem>
                                    <MenuItem value="RESOLVED">Resolved</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Feedback Type</InputLabel>
                                <Select
                                    value={feedbackTypeFilter}
                                    label="Feedback Type"
                                    onChange={(e) => setFeedbackTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Feedback Types</MenuItem>
                                    <MenuItem value="POSITIVE">Positive</MenuItem>
                                    <MenuItem value="NEGATIVE">Negative</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Page Size</InputLabel>
                                <Select
                                    value={size}
                                    label="Page Size"
                                    onChange={(e) => setSize(e.target.value)}
                                >
                                    <MenuItem value={5}>5 per page</MenuItem>
                                    <MenuItem value={10}>10 per page</MenuItem>
                                    <MenuItem value={25}>25 per page</MenuItem>
                                    <MenuItem value={50}>50 per page</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Feedback Table */}
            <Paper elevation={3} sx={{ borderRadius: 3 }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" fontWeight={600}>
                        📋 AI Training Feedback ({totalElements} total)
                    </Typography>
                </Box>

                {loading && <LinearProgress />}

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Classification</TableCell>
                                <TableCell>Feedback</TableCell>
                                <TableCell>AI Prediction</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feedbacks.map((feedback) => (
                                <TableRow key={feedback.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                <Person />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {feedback.userDisplayName || 'Unknown User'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {feedback.userEmail || 'No email'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {feedback.imageFileName && (
                                                <Avatar
                                                    src={adminFeedbackAPI.getClassificationImageUrl(feedback.imageFileName)}
                                                    sx={{ width: 40, height: 40 }}
                                                    variant="rounded"
                                                >
                                                    <PhotoCamera />
                                                </Avatar>
                                            )}
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    ID: {feedback.classificationId}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(feedback.classifiedAt)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Chip
                                                icon={feedback.correct ? <ThumbUp /> : <ThumbDown />}
                                                label={feedback.correct ? 'Positive' : 'Negative'}
                                                color={feedback.correct ? 'success' : 'error'}
                                                size="small"
                                                sx={{ mb: 1 }}
                                            />
                                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                                {feedback.message.length > 100
                                                    ? `${feedback.message.substring(0, 100)}...`
                                                    : feedback.message}
                                            </Typography>
                                            {feedback.providedCategory && (
                                                <Chip
                                                    label={`Correct: ${feedback.providedCategory}`}
                                                    size="small"
                                                    sx={{ mt: 1, fontSize: '0.7rem', backgroundColor: '#e3f2fd' }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Chip
                                                label={feedback.predictedCategory || 'Unknown'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: feedback.categoryColor ? `${feedback.categoryColor}20` : '#f5f5f5',
                                                    color: feedback.categoryColor || '#666',
                                                    mb: 1
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Confidence: {Math.round(feedback.confidence || 0)}%
                                            </Typography>
                                            {feedback.aiModelVersion && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Model: {feedback.aiModelVersion}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="primary" display="block" sx={{ fontWeight: 600 }}>
                                                AI Original Prediction
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getStatusIcon(feedback.status)}
                                            label={feedback.status}
                                            sx={{
                                                backgroundColor: `${getStatusColor(feedback.status)}20`,
                                                color: getStatusColor(feedback.status),
                                                fontWeight: 600
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {!feedback.correct && feedback.confidence && (
                                            <Badge
                                                badgeContent={feedback.confidence > 90 ? 'CRITICAL' :
                                                            feedback.confidence > 70 ? 'HIGH' : 'MEDIUM'}
                                                color={feedback.confidence > 90 ? 'error' :
                                                      feedback.confidence > 70 ? 'warning' : 'info'}
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        fontSize: '0.6rem',
                                                        height: 16,
                                                        minWidth: 16
                                                    }
                                                }}
                                            >
                                                <SmartToy sx={{ color: getSeverityColor(feedback.confidence) }} />
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(feedback.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openDetailsDialog(feedback)}
                                                    sx={{ color: '#2196f3' }}
                                                >
                                                    <Info />
                                                </IconButton>
                                            </Tooltip>
                                            {feedback.status === 'OPEN' && (
                                                <Tooltip title="Mark as Reviewed">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusUpdate(feedback.id, 'REVIEWED')}
                                                        sx={{ color: '#ff9800' }}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {feedback.status !== 'RESOLVED' && (
                                                <Tooltip title="Resolve Feedback">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedFeedback(feedback);
                                                            setResolutionDialog(true);
                                                            setAdminResponse(feedback.response || '');
                                                        }}
                                                        sx={{ color: '#4caf50' }}
                                                    >
                                                        <CheckCircle />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(event) => {
                        setSize(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{
                        borderTop: '1px solid #e0e0e0',
                        '& .MuiTablePagination-toolbar': {
                            px: 3
                        }
                    }}
                />
            </Paper>

            {/* Feedback Details Dialog */}
            <FeedbackDetailsDialog
                open={detailsDialog}
                onClose={() => setDetailsDialog(false)}
                selectedFeedback={selectedFeedback}
                onResolveClick={handleResolveClick}
                formatDate={formatDate}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                getSeverityColor={getSeverityColor}
            />

            {/* Feedback Resolution Dialog */}
            <FeedbackResolutionDialog
                open={resolutionDialog}
                onClose={() => setResolutionDialog(false)}
                selectedFeedback={selectedFeedback}
                onSubmit={handleResolveFeedback}
                submitting={submitting}
                formatDate={formatDate}
            />
        </Box>
    );
};

export default AdminFeedbackManagement;
